export const DEFAULT_THEME = {
  primary: "#8B313A",
  secondary: "#31778C",
};

export const DEFAULT_PORTAL_TITLE = "UniqueCoop Portal";
export const DEFAULT_FAVICON = "/logo1.svg";

const COOP_STORAGE_KEY = "coop_details_profile";

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

export function isValidHexColor(value) {
  return typeof value === "string" && HEX_COLOR_REGEX.test(value.trim());
}

export function normalizeHexColor(value, fallback) {
  if (!value) return fallback;
  const trimmed = String(value).trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return isValidHexColor(withHash) ? withHash.toUpperCase() : fallback;
}

/**
 * Normalize theme from API payload, login profile, or legacy flat keys.
 */
export function normalizeTheme(source) {
  if (!source) {
    return { ...DEFAULT_THEME };
  }

  const themeBlock = source.theme || source;
  const primary = normalizeHexColor(
    themeBlock.primary_color ?? themeBlock.primary ?? source.primary_color ?? source.border_color,
    DEFAULT_THEME.primary
  );
  const secondary = normalizeHexColor(
    themeBlock.secondary_color ?? themeBlock.secondary ?? source.secondary_color ?? source.background_color,
    DEFAULT_THEME.secondary
  );

  return { primary, secondary };
}

export function hexToRgba(hex, alpha = 1) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary).replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary).replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`.toUpperCase();
}

function relativeLuminance({ r, g, b }) {
  const channels = [r, g, b].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG contrast ratio between two hex colors (1–21). */
export function getContrastRatio(hexA, hexB) {
  const l1 = relativeLuminance(hexToRgb(hexA));
  const l2 = relativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Pick white or dark text for readability on a colored background. */
export function getReadableTextColor(backgroundHex) {
  const whiteContrast = getContrastRatio(backgroundHex, "#FFFFFF");
  const darkContrast = getContrastRatio(backgroundHex, "#383838");
  return whiteContrast >= darkContrast ? "#FFFFFF" : "#383838";
}

/** True when used as text on a white background fails WCAG AA (4.5:1). */
export function hasPoorContrastWithWhite(hex) {
  return isValidHexColor(hex) && getContrastRatio(hex, "#FFFFFF") < 4.5;
}

/** Relative luminance above ~mid-grey — useful for detecting light brand colors. */
export function isLightColor(hex) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  return relativeLuminance(hexToRgb(normalized)) > 0.6;
}

/**
 * Darken a hex color. `amount` is 0–1 (higher = darker).
 * Original brand color is never mutated in storage — this is for derived tokens only.
 */
export function darkenColor(hex, amount = 0.15) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  const { r, g, b } = hexToRgb(normalized);
  const factor = Math.max(0, 1 - amount);
  return rgbToHex({
    r: r * factor,
    g: g * factor,
    b: b * factor,
  });
}

const READABLE_ON_WHITE_MIN_RATIO = 4.5;

/**
 * Returns the original hex when it meets contrast on white; otherwise a darkened readable variant.
 * Does not replace the saved brand color — use for text/links on white surfaces.
 */
export function getReadableColorOnWhite(hex, minRatio = READABLE_ON_WHITE_MIN_RATIO) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  if (getContrastRatio(normalized, "#FFFFFF") >= minRatio) {
    return normalized;
  }
  let current = normalized;
  for (let step = 0; step < 24; step += 1) {
    current = darkenColor(current, 0.08);
    if (getContrastRatio(current, "#FFFFFF") >= minRatio) {
      return current;
    }
  }
  return current;
}

export function buildThemeCssVariablesFromColors(primaryColor, secondaryColor) {
  return buildThemeCssVariables({
    primary: normalizeHexColor(primaryColor, DEFAULT_THEME.primary),
    secondary: normalizeHexColor(secondaryColor, DEFAULT_THEME.secondary),
  });
}

export function buildThemeCssVariables(theme) {
  const normalized = normalizeTheme(theme);
  const { primary, secondary } = normalized;
  const primaryReadable = getReadableColorOnWhite(primary);
  const secondaryReadable = getReadableColorOnWhite(secondary);

  return {
    "--color-primary": primary,
    "--color-secondary": secondary,
    "--color-primary-soft": hexToRgba(primary, 0.1),
    "--color-secondary-soft": hexToRgba(secondary, 0.1),
    "--color-border": hexToRgba(primary, 0.2),
    "--color-link": secondaryReadable,
    "--color-accent": secondaryReadable,
    "--color-primary-dark": `color-mix(in srgb, ${primary} 80%, black)`,
    "--color-secondary-dark": `color-mix(in srgb, ${secondary} 80%, black)`,
    "--color-primary-readable": primaryReadable,
    "--color-secondary-readable": secondaryReadable,
    "--color-primary-text": getReadableTextColor(primary),
    "--color-secondary-text": getReadableTextColor(secondary),
    "--primary": primary,
    "--secondary2": secondary,
  };
}

export function applyCssVariables(target, variables) {
  if (!target) return;
  Object.entries(variables).forEach(([key, value]) => {
    target.style.setProperty(key, value);
  });
}

export function applyCompanyTheme(theme, target = document.documentElement) {
  applyCssVariables(target, buildThemeCssVariables(theme));
  updateThemeColorMeta(normalizeTheme(theme).primary);
}

/** Branding fields from API `branding` block or legacy top-level keys. */
export function extractBranding(source) {
  if (!source) {
    return { name: null, logo_url: null, domain_url: null };
  }
  const branding = source.branding || source;
  return {
    name: branding.name ?? source.name ?? null,
    logo_url: branding.logo_url ?? source.logo_url ?? null,
    domain_url: branding.domain_url ?? source.domain_url ?? null,
  };
}

export function getBrandingFromCoopOrCompany(coopData, userCompany) {
  if (userCompany) {
    const fromUser = extractBranding(userCompany);
    if (fromUser.name || fromUser.logo_url) return fromUser;
  }
  if (coopData) return extractBranding(coopData);
  return { name: null, logo_url: null, domain_url: null };
}

export function updateDocumentTitle(companyName) {
  if (typeof document === "undefined") return;
  document.title = companyName ? `${companyName} Portal` : DEFAULT_PORTAL_TITLE;
}

export function updateFavicon(logoUrl) {
  if (typeof document === "undefined") return;
  const href = logoUrl || DEFAULT_FAVICON;

  let icon = document.querySelector('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  icon.href = href;

  let apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (!apple) {
    apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    document.head.appendChild(apple);
  }
  apple.href = href;
}

export function updateThemeColorMeta(primaryColor) {
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", normalizeHexColor(primaryColor, DEFAULT_THEME.primary));
  }
}

/** Apply theme colors + browser title + favicon + theme-color meta. */
export function applyTenantIdentity(coopData, userCompany) {
  const theme = getThemeFromCoopOrCompany(coopData, userCompany);
  applyCompanyTheme(theme);
  const branding = getBrandingFromCoopOrCompany(coopData, userCompany);
  updateDocumentTitle(branding.name);
  updateFavicon(branding.logo_url);
}

export function readCoopFromStorage() {
  try {
    const raw = localStorage.getItem(COOP_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function restoreThemeFromStorage() {
  const coopData = readCoopFromStorage();
  if (coopData) {
    applyTenantIdentity(coopData, null);
    return normalizeTheme(coopData);
  }
  applyCompanyTheme(DEFAULT_THEME);
  updateDocumentTitle(null);
  updateFavicon(null);
  return { ...DEFAULT_THEME };
}

export function persistThemeFromCoopPayload(coopPayload) {
  if (!coopPayload) return;
  applyTenantIdentity(coopPayload, null);
}

export function getThemeFromCoopOrCompany(coopData, userCompany) {
  if (userCompany?.theme || userCompany?.primary_color) {
    return normalizeTheme(userCompany);
  }
  if (coopData?.theme || coopData?.primary_color) {
    return normalizeTheme(coopData);
  }
  return { ...DEFAULT_THEME };
}

export function themeToApiPayload(theme) {
  const normalized = normalizeTheme(theme);
  return {
    primary_color: normalized.primary,
    secondary_color: normalized.secondary,
  };
}

export function mergeThemeIntoCoopPayload(coopPayload, theme) {
  if (!coopPayload) return coopPayload;
  const normalized = normalizeTheme(theme);
  return {
    ...coopPayload,
    theme: {
      primary_color: normalized.primary,
      secondary_color: normalized.secondary,
    },
  };
}

export function mergeThemeIntoCompany(company, theme) {
  if (!company) return company;
  const normalized = normalizeTheme(theme);
  return {
    ...company,
    theme: {
      primary_color: normalized.primary,
      secondary_color: normalized.secondary,
    },
  };
}

/** Inline script body for index.html anti-flash (must stay in sync with logic above). */
export const ANTI_FLASH_THEME_SCRIPT = `
(function () {
  var DEFAULT_PRIMARY = "#8B313A";
  var DEFAULT_SECONDARY = "#31778C";
  function normalizeHex(value, fallback) {
    if (!value) return fallback;
    var trimmed = String(value).trim();
    var withHash = trimmed.charAt(0) === "#" ? trimmed : "#" + trimmed;
    return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash.toUpperCase() : fallback;
  }
  function hexToRgba(hex, alpha) {
    var n = normalizeHex(hex, DEFAULT_PRIMARY).replace("#", "");
    var r = parseInt(n.slice(0, 2), 16);
    var g = parseInt(n.slice(2, 4), 16);
    var b = parseInt(n.slice(4, 6), 16);
    return "rgba(" + r + "," + g + "," + b + "," + alpha + ")";
  }
  function relativeLuminance(rgb) {
    var channels = [rgb.r, rgb.g, rgb.b].map(function (channel) {
      var n = channel / 255;
      return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }
  function contrastRatio(hexA, hexB) {
    var l1 = relativeLuminance(hexToRgb(normalizeHex(hexA, DEFAULT_PRIMARY)));
    var l2 = relativeLuminance(hexToRgb(normalizeHex(hexB, DEFAULT_PRIMARY)));
    var lighter = Math.max(l1, l2);
    var darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  }
  function darkenHex(hex, amount) {
    var rgb = hexToRgb(normalizeHex(hex, DEFAULT_PRIMARY));
    var factor = Math.max(0, 1 - amount);
    function toHex(n) {
      var v = Math.max(0, Math.min(255, Math.round(n)));
      var s = v.toString(16);
      return s.length === 1 ? "0" + s : s;
    }
    return ("#" + toHex(rgb.r * factor) + toHex(rgb.g * factor) + toHex(rgb.b * factor)).toUpperCase();
  }
  function hexToRgb(hex) {
    var n = normalizeHex(hex, DEFAULT_PRIMARY).replace("#", "");
    return {
      r: parseInt(n.slice(0, 2), 16),
      g: parseInt(n.slice(2, 4), 16),
      b: parseInt(n.slice(4, 6), 16),
    };
  }
  function readableOnWhite(hex) {
    var normalized = normalizeHex(hex, DEFAULT_PRIMARY);
    if (contrastRatio(normalized, "#FFFFFF") >= 4.5) return normalized;
    var current = normalized;
    for (var i = 0; i < 24; i++) {
      current = darkenHex(current, 0.08);
      if (contrastRatio(current, "#FFFFFF") >= 4.5) return current;
    }
    return current;
  }
  function readableTextOnBg(hex) {
    var normalized = normalizeHex(hex, DEFAULT_PRIMARY);
    var white = contrastRatio(normalized, "#FFFFFF");
    var dark = contrastRatio(normalized, "#383838");
    return white >= dark ? "#FFFFFF" : "#383838";
  }
  function applyTheme(primary, secondary) {
    var primaryReadable = readableOnWhite(primary);
    var secondaryReadable = readableOnWhite(secondary);
    var root = document.documentElement;
    root.style.setProperty("--color-primary", primary);
    root.style.setProperty("--color-secondary", secondary);
    root.style.setProperty("--color-primary-soft", hexToRgba(primary, 0.1));
    root.style.setProperty("--color-secondary-soft", hexToRgba(secondary, 0.1));
    root.style.setProperty("--color-border", hexToRgba(primary, 0.2));
    root.style.setProperty("--color-link", secondaryReadable);
    root.style.setProperty("--color-accent", secondaryReadable);
    root.style.setProperty("--color-primary-dark", "color-mix(in srgb, " + primary + " 80%, black)");
    root.style.setProperty("--color-secondary-dark", "color-mix(in srgb, " + secondary + " 80%, black)");
    root.style.setProperty("--color-primary-readable", primaryReadable);
    root.style.setProperty("--color-secondary-readable", secondaryReadable);
    root.style.setProperty("--color-primary-text", readableTextOnBg(primary));
    root.style.setProperty("--color-secondary-text", readableTextOnBg(secondary));
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--secondary2", secondary);
    var meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", primary);
  }
  function applyIdentity(data) {
    if (!data) {
      document.title = "UniqueCoop Portal";
      return;
    }
    var branding = data.branding || data;
    var name = branding.name || data.name;
    var logo = branding.logo_url || data.logo_url;
    document.title = name ? name + " Portal" : "UniqueCoop Portal";
    if (logo) {
      var icon = document.querySelector('link[rel="icon"]');
      if (icon) icon.href = logo;
      var apple = document.querySelector('link[rel="apple-touch-icon"]');
      if (apple) apple.href = logo;
    }
  }
  try {
    var raw = localStorage.getItem("coop_details_profile");
    if (!raw) {
      applyTheme(DEFAULT_PRIMARY, DEFAULT_SECONDARY);
      return;
    }
    var data = JSON.parse(raw);
    var theme = data.theme || data;
    applyTheme(
      normalizeHex(theme.primary_color || theme.primary || data.primary_color || data.border_color, DEFAULT_PRIMARY),
      normalizeHex(theme.secondary_color || theme.secondary || data.secondary_color || data.background_color, DEFAULT_SECONDARY)
    );
    applyIdentity(data);
  } catch (e) {
    applyTheme(DEFAULT_PRIMARY, DEFAULT_SECONDARY);
  }
})();
`;
